import {
    type ChangeEvent,
    type FC,
    useEffect,
    useRef,
    useState
} from 'react'

import { useTranslation } from 'react-i18next'

import { IconCameraPortraitOutline } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import DateModal from 'components/UI/Modal/Date/DateModal'
import { useModalContext } from 'context/modalContext'
import {
    attachPhotoFromS3,
    presignPhoto,
    uploadFileToS3
} from 'services/endpoints/attachment'
import { uploadPost } from 'services/endpoints/post'

import styles from './NewStream.module.scss'
import ScheduleBubble from '../ScheduleContent/Components/ScheduleBubble'

interface IPostPhotoPayload {
    photo_id: string
    order: number
    locked: boolean
    orientation: string | null
    post_preview_type: string | null
    price: number | null
}

interface IAttachPhotoFromS3Data {
    id: string
    status: string
    original_orientation: string | null
    image_hash: string | null
}
interface IStreamData {
    schedule_date: Date | null
    teaser_photo: IPostPhotoPayload | null
    teaser_image: string
    message: string
}

const UPCOMING_POST_OPTIONS = [
    {
        label: '30 minutes before',
        value: '0.5'
    },
    {
        label: '1 hour before',
        value: '1'
    },
    {
        label: '2 hours before',
        value: '2'
    },
    {
        label: '4 hours before',
        value: '4'
    },
    {
        label: '12 hours before',
        value: '12'
    },
    {
        label: '24 hours before',
        value: '24'
    }
]

const formatDateForApi = (value: Date | null) => {
    if (!value) return null

    return value instanceof Date
        ? value.toISOString()
        : value
}

const NewStream: FC<{
    date: Date
    groupId?: number
}> = ({
    date,
    groupId
}) => {
    const [streamData, setStreamData] =
        useState<IStreamData>({
            schedule_date: date,
            teaser_photo: null,
            teaser_image: '',
            message: ''
        })

    const [isSubmitting, setIsSubmitting] =
        useState(false)

    const [isTitleFocused, setIsTitleFocused] =
        useState(false)

    const [
        upcomingPostEnabled,
        setUpcomingPostEnabled
    ] = useState(false)

    const [
        upcomingPostHours,
        setUpcomingPostHours
    ] = useState('2')

    const { t } = useTranslation()
    const modalData = useModalContext()

    const openScheduleEditor = (startTab: 'date' | 'time') => {
        modalData.addSecondaryModal(
            startTab === 'date' ? 'Select Date' : 'Select Time',
            <DateModal
                startTab={startTab}
                noNextStep
                newDateVal={streamData.schedule_date ?? new Date()}
                confirmFn={value => {
                    setStreamData(prev => ({
                        ...prev,
                        schedule_date: value
                    }))
                }}
                closeFn={() => {
                    modalData.clearSecondaryModal()
                }}
            />,
            false
        )
    }

    const onUploadImage = (uploadedPhoto: IAttachPhotoFromS3Data) => {
        const postPhoto: IPostPhotoPayload = {
            photo_id: uploadedPhoto.id,
            order: 1,
            locked: false,
            orientation: uploadedPhoto.original_orientation,
            post_preview_type: null,
            price: null
        }

        setStreamData(prev => ({
            ...prev,
            teaser_photo: postPhoto
        }))
    }

    const clearPreviewImage = () => {
        setStreamData(prev => ({
            ...prev,
            teaser_photo: null,
            teaser_image: ''
        }))
    }

    const handleUpcomingToggle = () => {
        setUpcomingPostEnabled(prev => {
            const next = !prev

            if (!next) {
                clearPreviewImage()
            }

            return next
        })
    }

    const handleSchedule = async () => {
        if (isSubmitting) return

        try {
            setIsSubmitting(true)

            const payload = {
                body: streamData.message || '',

                photos:
                    upcomingPostEnabled &&
                    streamData.teaser_photo
                        ? [streamData.teaser_photo]
                        : [],

                videos: [],
                sounds: [],
                tags: [],
                categories: [],
                groups: groupId ? [groupId] : [],
                shareOnTwitter: false,

                live_scheduled_at:
                    formatDateForApi(
                        streamData.schedule_date
                    ),

                upcoming_post_enabled:
                    upcomingPostEnabled,

                upcoming_post_hours_before:
                    upcomingPostEnabled
                        ? Number(
                              upcomingPostHours
                          )
                        : null
            }

            console.log(
                'NewStream uploadPost payload',
                payload
            )

            await uploadPost(payload as any)

            modalData.clearModal()
        } catch (error) {
            console.error(
                'Failed to schedule stream',
                error
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.bubblesContainer}>
                <div
                    className={styles.scheduleBubbleWrapper}
                    role='button'
                    tabIndex={0}
                    onClick={() => openScheduleEditor('date')}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openScheduleEditor('date')
                        }
                    }}
                >
                    <ScheduleBubble
                        date={streamData.schedule_date ?? date}
                        display='date'
                        title='Date'
                    />
                </div>

                <div
                    className={styles.scheduleBubbleWrapper}
                    role='button'
                    tabIndex={0}
                    onClick={() => openScheduleEditor('time')}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openScheduleEditor('time')
                        }
                    }}
                >
                    <ScheduleBubble
                        date={streamData.schedule_date ?? date}
                        display='time'
                        title='Time'
                    />
                </div>
            </div>

            <div
                className={`${styles.streamTitleWrapper} ${
                    isTitleFocused
                        ? styles.streamTitleWrapperFocused
                        : ''
                }`}
            >
                {!isTitleFocused &&
                    !streamData.message && (
                        <span
                            className={
                                styles.streamTitleLabel
                            }
                        >
                            Stream Title
                        </span>
                    )}

                <input
                    type='text'
                    className={styles.streamTitle}
                    value={streamData.message}
                    onFocus={() =>
                        setIsTitleFocused(true)
                    }
                    onBlur={() =>
                        setIsTitleFocused(false)
                    }
                    onChange={event => {
                        setStreamData(prev => ({
                            ...prev,
                            message:
                                event.target.value
                        }))
                    }}
                />
            </div>

            <div
                className={`${styles.upcomingPost} ${
                    upcomingPostEnabled
                        ? styles.upcomingPostEnabled
                        : ''
                }`}
            >
                <div
                    className={
                        styles.upcomingPostHeader
                    }
                >
                    <div
                        className={
                            styles.upcomingPostCopy
                        }
                    >
                        <div
                            className={
                                styles.upcomingPostTitle
                            }
                        >
                            Upcoming Post

                            <span
                                className={`${styles.upcomingPostStatus} ${
                                    upcomingPostEnabled
                                        ? styles.upcomingPostStatusOn
                                        : ''
                                }`}
                            >
                                {upcomingPostEnabled
                                    ? 'ON'
                                    : 'OFF'}
                            </span>
                        </div>

                        <div
                            className={
                                styles.upcomingPostSubtitle
                            }
                        >
                            Let fans know before
                            you go live
                        </div>
                    </div>

                    <button
                        type='button'
                        role='switch'
                        aria-checked={
                            upcomingPostEnabled
                        }
                        aria-label='Enable upcoming post'
                        className={`${styles.toggle} ${
                            upcomingPostEnabled
                                ? styles.toggleActive
                                : ''
                        }`}
                        onClick={
                            handleUpcomingToggle
                        }
                    >
                        <span />
                    </button>
                </div>

                {upcomingPostEnabled && (
                    <div
                        className={
                            styles.upcomingPostContent
                        }
                    >
                        <div
                            className={
                                styles.timingRow
                            }
                        >
                            <span
                                className={
                                    styles.timingLabel
                                }
                            >
                                Post
                            </span>

                            <div
                                className={
                                    styles.timingSelectWrapper
                                }
                            >
                                <select
                                    className={
                                        styles.timingSelect
                                    }
                                    value={
                                        upcomingPostHours
                                    }
                                    onChange={event =>
                                        setUpcomingPostHours(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    aria-label='Upcoming post timing'
                                >
                                    {UPCOMING_POST_OPTIONS.map(
                                        option => (
                                            <option
                                                key={
                                                    option.value
                                                }
                                                value={
                                                    option.value
                                                }
                                            >
                                                {
                                                    option.label
                                                }
                                            </option>
                                        )
                                    )}
                                </select>

                                <span
                                    className={
                                        styles.selectChevron
                                    }
                                >
                                    ‹
                                </span>
                            </div>
                        </div>

                        <div
                            className={
                                styles.uploadCard
                            }
                        >
                            <UploadImage
                                onUploadFinish={
                                    onUploadImage
                                }
                                onRemove={
                                    clearPreviewImage
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            <div
                className={
                    styles.buttonsContainer
                }
            >
                <Button
                    text={t('cancel')}
                    color='grey'
                    font='mont-14-normal'
                    width='13'
                    height='3'
                    clickFn={() => {
                        modalData.clearModal()
                    }}
                />

                <Button
                    color='black'
                    text={
                        isSubmitting
                            ? 'Scheduling...'
                            : 'Schedule'
                    }
                    customClass='button button--black button--mont-14-normal button--width-13 button--height-3 sortModal--btn'
                    clickFn={handleSchedule}
                />
            </div>
        </div>
    )
}

export const UploadImage: FC<{
    onUploadFinish: (
        _data: IAttachPhotoFromS3Data
    ) => void
    onRemove?: () => void
}> = ({
    onUploadFinish,
    onRemove
}) => {
    const [isUploading, setIsUploading] =
        useState(false)

    const [previewSrc, setPreviewSrc] =
        useState('')

    const inputFile =
        useRef<HTMLInputElement | null>(null)

    const showFileUpload = () => {
        if (
            inputFile.current &&
            !isUploading
        ) {
            inputFile.current.click()
        }
    }

    const clearPreview = () => {
        setPreviewSrc(prev => {
            if (prev.startsWith('blob:')) {
                URL.revokeObjectURL(prev)
            }

            return ''
        })

        if (inputFile.current) {
            inputFile.current.value = ''
        }

        onRemove?.()
    }

    const uploadImage = async (
        file: File
    ) => {
        const presigned =
            await presignPhoto({
                type: 'post_image',
                contentType: file.type,
                size: file.size
            })

        await uploadFileToS3(
            presigned.url,
            file
        )

        return await attachPhotoFromS3({
            s3_key: presigned.key,
            type: 'post'
        })
    }

    const onFileInputChange = async (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const file =
            event.target.files &&
            event.target.files.length > 0
                ? event.target.files[0]
                : null

        if (!file || !file.type.startsWith('image/')) {
            return
        }

        const blobUrl = URL.createObjectURL(file)

        setPreviewSrc(prev => {
            if (prev.startsWith('blob:')) {
                URL.revokeObjectURL(prev)
            }

            return blobUrl
        })

        try {
            setIsUploading(true)

            const uploaded = await uploadImage(file)

            const uploadedData: IAttachPhotoFromS3Data = uploaded.data.data

            onUploadFinish(uploadedData)
        } catch (error) {
            console.error('Failed to upload teaser image', error)
            clearPreview()
        } finally {
            setIsUploading(false)

            if (inputFile.current) {
                inputFile.current.value = ''
            }
        }
    }

    useEffect(() => {
        return () => {
            if (
                previewSrc.startsWith('blob:')
            ) {
                URL.revokeObjectURL(
                    previewSrc
                )
            }
        }
    }, [previewSrc])

    return (
        <div
            className={
                styles.uploadBackground
            }
            style={{
                backgroundImage:
                    previewSrc
                        ? `url("${previewSrc}")`
                        : 'none',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <input
                ref={inputFile}
                className={
                    styles.hiddenFileInput
                }
                type='file'
                name='teaserImage'
                accept='image/*'
                onChange={
                    onFileInputChange
                }
                disabled={isUploading}
            />

            {!previewSrc ? (
                <div
                    className={
                        styles.content
                    }
                >
                    <button
                        type='button'
                        className={
                            styles.uploadButton
                        }
                        onClick={
                            showFileUpload
                        }
                        disabled={
                            isUploading
                        }
                        aria-label='Add preview image'
                    >
                        <span
                            className={
                                styles.iconContainer
                            }
                        >
                            +
                        </span>
                    </button>

                    <p
                        className={
                            styles.infoMessage
                        }
                    >
                        {isUploading
                            ? 'Uploading...'
                            : 'Add Preview Image'}
                    </p>

                    <span
                        className={
                            styles.uploadHint
                        }
                    >
                        Recommended 16:9
                    </span>
                </div>
            ) : (
                <>
                    <button
                        type='button'
                        className={
                            styles.removeImage
                        }
                        onClick={
                            clearPreview
                        }
                        aria-label='Remove preview image'
                    >
                        ×
                    </button>

                    <button
                        type='button'
                        className={
                            styles.imageIcon
                        }
                        onClick={
                            showFileUpload
                        }
                        disabled={
                            isUploading
                        }
                        aria-label='Change preview image'
                    >
                        <IconCameraPortraitOutline
                            color='#FFFFFF'
                        />
                    </button>
                </>
            )}
        </div>
    )
}

export default NewStream